import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Wallet, Send, ScrollText, LayoutDashboard, CheckCircle2, Clock, XCircle,
  ArrowUpRight, ArrowDownLeft, Loader2, FileDown, Building2, User, CreditCard, Truck,
} from "lucide-react";
import { RetailerCashPickup } from "../components/cashpickup/RetailerCashPickup";
import PhotonLogo from "../components/PhotonLogo";
import { useToast } from "../hooks/use-toast";
import { merchantMe, retailerWallet, retailerPayouts, createPayout, fileUrl } from "../api";

const fmt = (d) => { try { return new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return ""; } };
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TABS = [
  { key: "overview", label: "Dashboard", icon: LayoutDashboard },
  { key: "payout", label: "Payout", icon: Send },
  { key: "pickup", label: "Cash Pickup", icon: Truck },
  { key: "passbook", label: "Statement", icon: ScrollText },
  { key: "profile", label: "Profile", icon: User },
];

const STATUS_PILL = {
  success: "bg-green-100 text-green-700",
  processing: "bg-orange-100 text-orange-700",
  failed: "bg-red-100 text-red-700",
};

const MerchantDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState("overview");
  const [me, setMe] = useState(null);
  const [balance, setBalance] = useState(0);
  const [txns, setTxns] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ beneficiary_name: "", account_number: "", ifsc: "", amount: "", mode: "IMPS", remarks: "" });

  const load = useCallback(() => {
    retailerWallet().then((w) => { setBalance(w.balance); setTxns(w.transactions); }).catch(() => {});
    retailerPayouts().then(setPayouts).catch(() => {});
  }, []);

  useEffect(() => {
    merchantMe().then((d) => { setMe(d); setBalance(d.wallet_balance || 0); setLoading(false); load(); }).catch(() => navigate("/login"));
  }, []);

  const logout = () => {
    localStorage.removeItem("pp_merchant_token");
    localStorage.removeItem("pp_role");
    navigate("/login");
  };

  const downloadStatement = () => {
    const rows = [["Date", "Description", "Type", "Direction", "Amount", "Balance"]];
    txns.forEach((t) => rows.push([new Date(t.created_at).toLocaleString("en-IN"), t.reason, t.type, t.direction, t.amount, t.balance_after]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "photonpe_statement.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const submitPayout = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!form.beneficiary_name || !form.account_number || !form.ifsc || !amt) {
      toast({ title: "Please fill all payout fields" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await createPayout({ ...form, amount: amt });
      setBalance(res.balance);
      toast({ title: "Payout initiated 🎉", description: `${inr(amt)} to ${form.beneficiary_name}. New balance ${inr(res.balance)}` });
      setForm({ beneficiary_name: "", account_number: "", ifsc: "", amount: "", mode: "IMPS", remarks: "" });
      load();
      setTab("payout");
    } catch (err) {
      toast({ title: "Payout failed", description: err.response?.data?.detail || "Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#475569]">Loading…</div>;
  if (!me) return null;

  const field = "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-[#0f2540] outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 transition";
  const labelCls = "block text-sm font-semibold text-[#0D1B2A] mb-1.5";
  const successCount = payouts.filter((p) => p.status === "success").length;

  return (
    <div className="min-h-screen bg-[#f6f9fd] flex">
      {/* Sidebar */}
      <aside className="w-60 bg-white border-r border-slate-100 min-h-screen sticky top-0 hidden md:flex flex-col">
        <div className="p-5 border-b border-slate-100"><PhotonLogo variant="dark" /></div>
        <nav className="p-3 flex-1">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`retailer-tab-${t.key}`} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold mb-1 transition-colors ${tab === t.key ? "bg-[#0D1B2A] text-white" : "text-[#1E293B] hover:bg-[#F0FDFA]"}`}>
              <t.icon size={17} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100">
          <button onClick={logout} data-testid="merchant-logout-button" className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-red-500 hover:bg-red-50"><LogOut size={17} /> Logout</button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        {/* Topbar */}
        <div className="bg-white border-b border-slate-100 px-6 py-3.5 flex items-center justify-between sticky top-0 z-20">
          <h1 className="text-lg font-extrabold text-[#0D1B2A] capitalize">{TABS.find((t) => t.key === tab)?.label}</h1>
          <div className="flex items-center gap-3">
            <div data-testid="wallet-balance-chip" className="flex items-center gap-2 bg-[#F0FDFA] text-[#0D1B2A] font-bold text-sm px-4 py-2 rounded-full">
              <Wallet size={16} className="text-[#0EA5E9]" /> {inr(balance)}
            </div>
            <button onClick={logout} className="md:hidden text-red-500 p-2"><LogOut size={18} /></button>
          </div>
        </div>

        {/* Mobile tabs */}
        <div className="md:hidden flex gap-2 overflow-x-auto px-4 pt-4">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${tab === t.key ? "bg-[#0D1B2A] text-white" : "bg-white text-[#1E293B]"}`}>{t.label}</button>
          ))}
        </div>

        <div className="p-6">
          {/* Overview */}
          {tab === "overview" && (
            <div className="space-y-6">
              <h2 className="text-xl font-extrabold text-[#0D1B2A]" data-testid="merchant-welcome">Welcome, {me.full_name} 👋</h2>
              <div className="grid sm:grid-cols-3 gap-5">
                <div className="rounded-2xl p-6 text-white shadow-lg" style={{ background: "linear-gradient(135deg,#0D1B2A,#0EA5E9)" }} data-testid="wallet-balance-card">
                  <div className="flex items-center gap-2 text-white/80 text-sm font-medium"><Wallet size={16} /> Wallet Balance</div>
                  <div className="text-3xl font-extrabold mt-2">{inr(balance)}</div>
                  <div className="text-xs text-white/60 mt-1">Contact admin to load funds</div>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                  <div className="text-sm text-[#64748B] font-medium">Total Payouts</div>
                  <div className="text-3xl font-extrabold text-[#0D1B2A] mt-2">{payouts.length}</div>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                  <div className="text-sm text-[#64748B] font-medium">Successful</div>
                  <div className="text-3xl font-extrabold text-green-600 mt-2">{successCount}</div>
                </div>
              </div>
              <button onClick={() => setTab("payout")} className="inline-flex items-center gap-2 text-white font-semibold px-6 py-3 rounded-full" style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}>
                <Send size={16} /> New Payout
              </button>
            </div>
          )}

          {/* Payout */}
          {tab === "payout" && (
            <div className="grid lg:grid-cols-[380px_1fr] gap-6">
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm h-fit">
                <h2 className="font-bold text-[#0D1B2A] mb-4">New Payout</h2>
                <form onSubmit={submitPayout} className="space-y-4">
                  <div><label className={labelCls}>Beneficiary Name</label><input data-testid="payout-name-input" className={field} value={form.beneficiary_name} onChange={(e) => setForm({ ...form, beneficiary_name: e.target.value })} /></div>
                  <div><label className={labelCls}>Account Number</label><input data-testid="payout-account-input" className={field} value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} /></div>
                  <div><label className={labelCls}>IFSC Code</label><input data-testid="payout-ifsc-input" className={field} value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className={labelCls}>Amount (₹)</label><input data-testid="payout-amount-input" type="number" className={field} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
                    <div>
                      <label className={labelCls}>Mode</label>
                      <select data-testid="payout-mode-select" className={field} value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
                        {["IMPS", "NEFT", "RTGS", "UPI"].map((m) => <option key={m}>{m}</option>)}
                      </select>
                    </div>
                  </div>
                  <div><label className={labelCls}>Remarks (optional)</label><input className={field} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
                  <button type="submit" disabled={submitting} data-testid="payout-submit-button" className="w-full flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-xl disabled:opacity-70" style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}>
                    {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send Payout
                  </button>
                  <p className="text-xs text-[#64748B] text-center">Amount + applicable fee is debited from your wallet.</p>
                </form>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <h2 className="font-bold text-[#0D1B2A] mb-4">Payout History</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="payout-history-table">
                    <thead><tr className="text-left text-[#94A3B8] text-xs uppercase border-b border-slate-100">
                      <th className="py-2 pr-3">Beneficiary</th><th className="py-2 pr-3">Amount</th><th className="py-2 pr-3">Mode</th><th className="py-2 pr-3">Status</th><th className="py-2">Date</th>
                    </tr></thead>
                    <tbody>
                      {payouts.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-[#64748B]">No payouts yet.</td></tr>}
                      {payouts.map((p) => (
                        <tr key={p.id} className="border-b border-slate-50">
                          <td className="py-3 pr-3"><div className="font-semibold text-[#0D1B2A]">{p.beneficiary_name}</div><div className="text-xs text-[#94A3B8]">{p.account_number} · {p.ifsc}</div></td>
                          <td className="py-3 pr-3 font-semibold text-[#0D1B2A]">{inr(p.amount)}</td>
                          <td className="py-3 pr-3">{p.mode}</td>
                          <td className="py-3 pr-3"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS_PILL[p.status] || "bg-slate-100 text-slate-600"}`}>{p.status}</span></td>
                          <td className="py-3 text-xs text-[#94A3B8]">{fmt(p.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {tab === "pickup" && <RetailerCashPickup me={me} onWalletChange={load} />}

          {/* Passbook / Statement */}
          {tab === "passbook" && (
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-[#0D1B2A]">Account Statement</h2>
                <button onClick={downloadStatement} data-testid="statement-download" className="flex items-center gap-1.5 text-sm font-semibold text-[#0EA5E9] bg-[#F0FDFA] px-3 py-2 rounded-lg"><FileDown size={14} /> Download CSV</button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="passbook-table">
                  <thead><tr className="text-left text-[#94A3B8] text-xs uppercase border-b border-slate-100">
                    <th className="py-2 pr-3">Description</th><th className="py-2 pr-3">Type</th><th className="py-2 pr-3">Amount</th><th className="py-2 pr-3">Balance</th><th className="py-2">Date</th>
                  </tr></thead>
                  <tbody>
                    {txns.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-[#64748B]">No transactions yet.</td></tr>}
                    {txns.map((t) => (
                      <tr key={t.id} className="border-b border-slate-50">
                        <td className="py-3 pr-3 text-[#1E293B]">{t.reason}</td>
                        <td className="py-3 pr-3 text-xs uppercase text-[#94A3B8]">{t.type}</td>
                        <td className={`py-3 pr-3 font-semibold flex items-center gap-1 ${t.direction === "credit" ? "text-green-600" : "text-red-500"}`}>
                          {t.direction === "credit" ? <ArrowDownLeft size={14} /> : <ArrowUpRight size={14} />} {inr(t.amount)}
                        </td>
                        <td className="py-3 pr-3 text-[#0D1B2A]">{inr(t.balance_after)}</td>
                        <td className="py-3 text-xs text-[#94A3B8]">{fmt(t.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Profile */}
          {tab === "profile" && (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-3">
                <h2 className="font-bold text-[#0D1B2A] mb-2">Account</h2>
                {[["Full Name", me.full_name], ["Business", me.business_name], ["Email", me.email], ["Phone", me.phone], ["Address", [me.address, me.city, me.state, me.pincode].filter(Boolean).join(", ")]].map(([k, v]) => (
                  <div key={k}><div className="text-[11px] uppercase text-[#94A3B8] font-semibold">{k}</div><div className="text-[#0D1B2A] font-medium text-sm">{v || "—"}</div></div>
                ))}
              </div>
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <h2 className="font-bold text-[#0D1B2A] mb-3">Documents</h2>
                <div className="flex flex-wrap gap-2">
                  {(me.documents || []).length === 0 && <span className="text-sm text-[#64748B]">No documents on file.</span>}
                  {(me.documents || []).map((d) => (
                    <a key={d.file_id} href={fileUrl(d.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm font-semibold text-[#0EA5E9] bg-[#F0FDFA] px-3 py-2 rounded-lg"><FileDown size={14} /> {d.label}</a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default MerchantDashboard;
